import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";

import { useModalStack, noop } from "utils/common";
import { D_TREE_URL, DTREE_ROW_COUNT_OPTIONS } from "utils/constants";
import { useCollectionFiltering } from "utils/DataGrid";
import EmptySection from "widgets/EmptySection";

import {
	Modal,
	Button,
	Toolbar,
	ProgressIndicator,
	Pager
} from "rhombus-react";

import Grid from "./Grid";
import { DTrees } from "reducers/DTrees";
import ViewVersionsModal from "./ViewVersionsModal";
import LinkedCreativesModal from "./LinkedCreativesModal";
import DuplicateDTreeModal from "./DuplicateDTreeModal";
import { getDefaultVersion } from "selectors/CreativeVersions";
import { fetchNamedVersion, loadCreativeVersions } from "reducers/CreativeVersions";
import useActiveCreative from "hooks/useActiveCreative";
import useFetchFallback from "hooks/useFetchFallback";

import "./DTreeModal.css";

export default function DTreeModal({
	libraryId,
	creativeId,

	onClose,
}){
	const dispatch = useDispatch();
	const {
		dTreesLoaded,
		isAssigning,
		dtrees,
		versionsAreLoaded,
		bulkCreativeIds,
	} = useSelector((state) => {
		return {
			dTreesLoaded:		state.dtrees.isLoaded,
			isAssigning:		state.dtrees.isAssigning,
			dtrees:				state.dtrees,
			versionsAreLoaded:	state.creativeVersions.isLoaded,
			bulkCreativeIds: state.modal.modalOptions.creativeIds,
		};
	});

	useActiveCreative(creativeId);

	function filterCreativeIds(){
		return [creativeId, bulkCreativeIds].filter((id) => {
			return id !== undefined;
		}).flat();
	}

	const creativeIds = useMemo(() => filterCreativeIds(), [creativeId, bulkCreativeIds]);

	const defaultVersion = useFetchFallback({
		selector:		getDefaultVersion,
		fetcher:		() => dispatch(fetchNamedVersion(libraryId, creativeId, "default")),
		shouldFetch:	!bulkCreativeIds && dTreesLoaded && versionsAreLoaded,
	});

	let isLoaded = !isAssigning && versionsAreLoaded;
	let isBulkLoaded = bulkCreativeIds ? !isAssigning : true;

	const commonModalProps = {
		title:  "Decision Trees",
		type: "modal",
		className: "dtree-modal",
		xClose: true,
		allowFullScreen: false,
		bladeProps: {
			verticallyCenter: true,
			focusTrapPaused:true,
		},
		onClose: closeModal,
		setAppTitle: false,
	};

	const {
		filterInfo: {
			page,
			rpp,
			totalPages,
			sort,
		},
		// columnFilters,
	} = useCollectionFiltering(
		dtrees,
		DTrees.setAdvancedFilter,
		() => {
			dispatch(DTrees.loadDTrees(libraryId, creativeIds));
		}
	);

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: totalPages,
		maxItems: dtrees?.filterInfo?.totalItems,
		disabled: !dTreesLoaded,
		rowCountItems: DTREE_ROW_COUNT_OPTIONS,
		onRowCountChange: newRpp => dispatch(DTrees.setRPP(newRpp)),
		onPageChange: newPage => dispatch(DTrees.setPage(newPage)),
	};

	// eslint-disable-next-line no-unused-vars
	const [modalStack, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });

	function closeModal(){
		onClose();
	}

	useEffect(() => {
		if(!versionsAreLoaded && !bulkCreativeIds) {
			dispatch(loadCreativeVersions(libraryId, creativeId));
		}
		return () => {
			dispatch(DTrees.clearModal());
		};
	}, []);

	let displayedContent;

	function DTreeLink({ children }){
		let url = `${D_TREE_URL}/v/new?own=${libraryId}&creative=${creativeIds}`;
		return <a href={url} target="_BLANK">{children}</a>;
	}

	function openViewVersionsModal(modalContext){
		function Configured(props){
			let modalProps = {
				...props,
				...modalContext,
			};
			return <ViewVersionsModal {...modalProps} />;
		}
		pushModal(Configured);
	}
	function openLinkedCreativesModal(dtree){
		function Configured(props){
			let modalProps = {
				...props,
				...dtree,
			};
			return <LinkedCreativesModal {...modalProps} />;
		}
		pushModal(Configured);
	}

	function duplicateDtreeModal(modalContext){
		function Configured(props){
			let modalProps = {
				...props,
				...modalContext,
				modalStack,
				libraryId:parseInt(libraryId),
			};
			return <DuplicateDTreeModal {...modalProps} />;
		}
		pushModal(Configured);
	}

	const gridActions = {
		openViewVersionsModal,
		openLinkedCreativesModal,
		duplicateDtreeModal,
		closeModal,
		defaultVersion,
	};

	if(dTreesLoaded && (versionsAreLoaded || bulkCreativeIds)){
		if(!dtrees?.items?.length){
			const emptyProps = {
				className: "empty-dtrees",
				header: "No decision trees have been created",
				src: "/images/noDTrees.svg",
				buttons: [
					<DTreeLink key="new"><Button>New Decision Tree</Button></DTreeLink>
				],
			};

			displayedContent = <EmptySection {...emptyProps} />;
		} else {
			displayedContent = (
				<>
					<Toolbar>
						<DTreeLink><Button icon="dtree" style="rounded">New Decision Tree</Button></DTreeLink>
					</Toolbar>
					<div className="grid">
						<Grid
							dtrees={dtrees?.items}
							creativeId={!bulkCreativeIds ? creativeId : creativeIds}
							libraryId={libraryId}
							isLoaded={bulkCreativeIds ? isBulkLoaded : isLoaded}
							actions={gridActions}
							sort={sort}
						/>
					</div>
				</>
			);
		}
	}

	function Body() {
		return (
			<div className="body-container">
				<ProgressIndicator isLoading={!dTreesLoaded} />
				{displayedContent}
			</div>
		);
	}

	function Footer() {
		return (
			<>
				<Button
					theme="default"
					type="reset"
					onClick = { closeModal }
				>Close</Button>
				{!dtrees?.items?.length || !dTreesLoaded ? null : <Pager {...pagerProps} />}
			</>
		);
	}

	if(ModalStackHead){
		return <ModalStackHead />;
	}

	return (
		<Modal
			{...commonModalProps}
			renderContent = { Body }
			renderFooter = { Footer }
		/>
	);
}

DTreeModal.displayName = "DTreeModal";
DTreeModal.defaultProps = {
	libraryId: -1,
	creativeIds: [],
	onClose: noop,
	columnFilters: {},
};

DTreeModal.propTypes = {
	libraryId: PropTypes.string,
	creativeIds: PropTypes.arrayOf(PropTypes.string),
	onClose: PropTypes.func,
	columnFilters: PropTypes.object,
};
